import { Box, HStack, Text } from "@chakra-ui/react"
import { formatDistanceStrict } from "date-fns"
import { useContext, useEffect, useState } from "react"
import AppColors from "../../../../constants/AppColors"
import { DotMenuIcon, RebuttalIcon } from "../../../../constants/IconData"
import { useComponentVisible } from "../../../../hooks"
import { AuditSheetContext } from "../../../../providers/AuditSheetProvider"
import CommentInput from "./CommentInput"
import CommentSaveButton from "./CommentSaveButton"
import CustomMenu from "./CustomMenu"
import { getTaggedUser } from '../../getTaggedUsers';
import { useToastr } from '@laxmimanogna/code-quick-components';
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons"
import RenderMessage from "../RenderMessage"
import { successToast } from "../../../../components/successToast"
import { RESOLVED_STATUS, REBUTTAL_CLOSED_STATUS, dynamicAuditColumnHeader } from "../../constants"

const CommentBox = ({ onEditModalOpen, onDeleteModalOpen, comment, index, selectedComment, setSelectedComment, setSelectedParentComment, batchId = undefined }) => {
    const [hiddenBoxKey, setHiddenBoxKey] = useState('')
    const auditSheetContext = useContext(AuditSheetContext);
    const toast = useToastr();

    const { setSelectedCell, postComment, isPostCommentLoading, allComments, setAllComments, allUsers, commentFlags, setCommentFlags, sheetData, setCurrentUpload, currentUpload } =
        auditSheetContext;
    const isRowWise = comment.audit_sheet_rows?.length > 0 && !comment.audit_sheet_columns?.length;
    const isChartUnderReview = currentUpload.status === 'CLIENT REBUTTAL' || currentUpload.status === 'REBUTTAL IN PROGRESS';

    const getType = () => {
        if (comment.action === 'BATCH NOTES') return 'Batch Notes';
        if (!comment.audit_sheet_rows?.length && !comment.audit_sheet_columns?.length) return 'Note';
        if (comment.action === 'REBUTTAL') return 'Rebuttal';
        return 'Comment';
    }

    const getStatus = () => {
        const replies = comment.reply_comments || [];
        let status = 'Open';
        for (const reply of replies) {
            const action = reply.action;
            if (action === 'REBUTTAL-ACCEPTED') status = 'Accepted';
            else if (action === 'REBUTTAL-REJECTED') status = 'Rejected';
            else if (action === 'REBUTTAL-RESOLVED') status = 'Resolved';
            else if (RESOLVED_STATUS.includes(action)) status = 'Resolved';
            else if (action === 'RE-OPENED') status = 'Re-opened';
        }
        return status;
    }

    const threadStatus = getStatus();
    const isRejected = threadStatus === 'Rejected';
    // Thread is open and chart is already under QA review
    const alreadyInClientReview = comment.action === 'REBUTTAL' &&
        isChartUnderReview &&
        threadStatus !== 'Resolved' && !isRejected;
    // Thread is open but chart is not yet under review — reply will trigger QA review
    const showQAHint = comment.action === 'REBUTTAL' && !alreadyInClientReview && !isRejected &&
        threadStatus !== 'Resolved';
    const [replyMessage, setReplyMessage] = useState('')
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

    const onEditClick = (currentComment) => {
        setSelectedComment(currentComment)
        if (comment.id !== currentComment.id) {
            setSelectedParentComment(comment)
        } else {
            setSelectedParentComment({})
        }
        onEditModalOpen()
        setIsComponentVisible(false)
    }

    const onDeleteClick = (currentComment) => {
        setSelectedComment(currentComment)
        if (comment.id !== currentComment.id) {
            setSelectedParentComment(comment)
        } else {
            setSelectedParentComment({})

        }
        onDeleteModalOpen()
        setIsComponentVisible(false)
    }

    const onReplyClick = async (currentComment) => {
        setSelectedComment(currentComment)
        const input = document.getElementById(`${index}-mention-input`);
        input.focus(); // adding the focus
        setIsComponentVisible(false)
    }

    const onResolveClick = async (currentComment) => {
        setSelectedComment(currentComment)
        const {
            audit_sheet_columns,
            audit_sheet_rows } = comment
        const updateBody = {
            audit_sheet_columns,
            audit_sheet_rows,
            chart: comment.chart,
            action: comment.action === 'REBUTTAL' ? 'REBUTTAL-RESOLVED' : 'RESOLVED',
            parent: comment.id,
        }
        setIsComponentVisible(false)
        const response = await postComment(updateBody)

        if (response.id) {
            setAllComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                    : c
            ));

            const updatedFlags = { ...commentFlags };
            comment.audit_sheet_rows.forEach(rowId => {
                updatedFlags[rowId] = { ...(commentFlags[rowId] || {}) };
                if (comment.audit_sheet_columns?.length) {
                    comment.audit_sheet_columns.forEach(col => { updatedFlags[rowId][col] = false; });
                } else {
                    updatedFlags[rowId]['__row__'] = false;
                }
            });
            setCommentFlags(updatedFlags);
        }
    }

    const onReopenClick = async (currentComment) => {
        setSelectedComment(currentComment)
        setIsComponentVisible(false)
        const {
            audit_sheet_columns,
            audit_sheet_rows } = comment
        const updateBody = {
            audit_sheet_columns,
            audit_sheet_rows,
            chart: comment.chart,
            action: 'RE-OPENED',
            parent: comment.id,
        }
        const response = await postComment(updateBody)
        if (response.id) {
            setAllComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                    : c
            ));

            const updatedFlags = { ...commentFlags };
            comment.audit_sheet_rows.forEach(rowId => {
                updatedFlags[rowId] = { ...(commentFlags[rowId] || {}) };
                if (comment.audit_sheet_columns?.length) {
                    comment.audit_sheet_columns.forEach(col => { updatedFlags[rowId][col] = true; });
                } else {
                    updatedFlags[rowId]['__row__'] = true;
                }
            });
            setCommentFlags(updatedFlags);
        }
    }

    const replyAction = async () => {
        //call api to reply
        const taggedUsers = getTaggedUser(replyMessage)
        const {
            audit_sheet_columns,
            audit_sheet_rows } = comment
        const updateBody = {
            audit_sheet_columns,
            audit_sheet_rows,
            chart: comment.chart,
            tagged_user: taggedUsers,
            comment: replyMessage,
            action: 'OPEN',
            parent: comment.id,
            batch: comment.batch,
        }

        const response = await postComment(updateBody)
        if (response.id) {
            setReplyMessage('')
            if (comment.action === 'REBUTTAL' && currentUpload.status !== 'CLIENT REBUTTAL' && currentUpload.status !== 'REBUTTAL IN PROGRESS') {
                setCurrentUpload(prev => ({ ...prev, status: 'CLIENT REBUTTAL' }));
            }
            const toastProps = {
                header: 'Success!',
                description: 'Reply sent',
            }
            successToast(toast, toastProps)
            setAllComments(prev => prev.map(c =>
                c.id === comment.id
                    ? { ...c, reply_comments: [...(c.reply_comments || []), { ...response }] }
                    : c
            ));
        }


    }

    const showResolveButton = (_commentData) => false;  // Client cannot resolve rebuttals — QA accepts/rejects

    const showReopenButton = (_commentData) => false;  // Client cannot reopen — open a new thread instead

    const bodyBgColor = () => {
        if (getStatus() === 'Resolved') return AppColors.loginFormBg;
        return 'white';
    }

    const statusBadgeColor = () => {
        if (threadStatus === 'Accepted') return '#16a34a';
        if (threadStatus === 'Rejected') return AppColors.danger;
        if (threadStatus === 'Resolved') return AppColors.darkGray;
        return 'rgba(255,255,255,0.25)';
    }

    const headerBgColor = () => {
        if (comment.action === 'BATCH NOTES') return '#d97706';
        if (getType() === 'Note') return AppColors.yellowColor;
        if (threadStatus === 'Resolved' || threadStatus === 'Rejected') return AppColors.darkGray;
        return AppColors.secondary;
    }

    const renderDotMenu = (commentData) => (
        <Box position='relative' p={1} flexShrink={0} onClick={() => setSelectedComment(commentData)}>
            <DotMenuIcon
                style={{ height: 14, width: 14, cursor: 'pointer' }}
                onClick={() => setIsComponentVisible(true)}
            />
            {isComponentVisible && selectedComment.key === `${commentData.index}-${commentData.i}-inline-comment` && (
                <CustomMenu
                    onEditClick={() => onEditClick(commentData)}
                    onDeleteClick={() => onDeleteClick(commentData)}
                    onReplyClick={() => onReplyClick(commentData)}
                    onResolveClick={() => onResolveClick(commentData)}
                    onReopenClick={() => onReopenClick(commentData)}
                    comment={commentData}
                    ref={ref}
                />
            )}
        </Box>
    )

    const renderComment = (obj) => {
        const STATUS = {
            'RE-OPENED': 'Re-opened',
            'RESOLVED': 'Marked as resolved',
            'REBUTTAL-RESOLVED': 'Resolved',
            'REBUTTAL-ACCEPTED': 'Accepted — correction in progress',
            'REBUTTAL-REJECTED': 'Rejected',
        }
        return (
            <Box
                key={obj.key}
                px={3} py={2}
                bg={obj.isParent ? bodyBgColor() : 'gray.50'}
                borderTop={obj.isParent ? 'none' : '1px solid'}
                borderColor='gray.100'
            >
                <HStack justifyContent='space-between' align='flex-start' mb={obj.justStatus ? 0 : 1}>
                    <HStack spacing={1} align='baseline' flexWrap='wrap'>
                        <Text fontSize='sm' fontWeight='semibold' color={AppColors.secondary} lineHeight='1.3'>
                            {obj?.user?.first_name} {obj?.user?.last_name}
                        </Text>
                        <Text fontSize='xs' color='gray.400'>
                            {obj.commented_date
                                ? formatDistanceStrict(new Date(obj.commented_date), new Date(), { addSuffix: true })
                                : ''}
                        </Text>
                    </HStack>
                    {!obj.justStatus && !(obj.isParent && comment.action === 'REBUTTAL') && renderDotMenu(obj)}
                </HStack>
                {obj.justStatus
                    ? <Text as='i' fontSize='xs' color={AppColors.darkGray}>{STATUS[obj.status]}</Text>
                    : <RenderMessage comment={obj} allUsers={allUsers} />
                }
            </Box>
        )
    }

    const isCollapsed = hiddenBoxKey === `${index}-comment-box`

    return (
        <Box
            mb={4}
            key={`${index}-comment-box`}
            cursor={comment?.audit_sheet_rows?.length ? 'pointer' : 'default'}
            onClick={() => {
                if (comment?.audit_sheet_rows?.length && comment?.audit_sheet_columns?.length) {
                    setSelectedCell({ row: comment.audit_sheet_rows[0], col: comment.audit_sheet_columns[0] })
                }
            }}
        >
            <Box borderRadius='lg' shadow='sm' border='1px solid' borderColor='gray.200'>

                {/* Compact single-row header */}
                <HStack
                    borderTopRadius='lg'
                    px={3} py={1.5}
                    bg={headerBgColor()}
                    justifyContent='space-between'
                    cursor='pointer'
                    onClick={(e) => {
                        e.stopPropagation()
                        setHiddenBoxKey(isCollapsed ? '' : `${index}-comment-box`)
                    }}
                >
                    <HStack spacing={2} flex={1} minW={0} overflow='hidden'>
                        <Box px={2} py='1px' bg='rgba(0,0,0,0.18)' borderRadius='full' flexShrink={0}>
                            <Text fontSize='10px' fontWeight='bold' color='white' letterSpacing='wide' whiteSpace='nowrap'>
                                {getType().toUpperCase()}
                            </Text>
                        </Box>
                        {batchId && comment.action !== 'BATCH NOTES' && comment.file_name && (
                            <Text fontSize='xs' color='rgba(255,255,255,0.9)' fontWeight='medium' noOfLines={1} minW={0}>
                                {comment.file_name}
                            </Text>
                        )}
                        {getType() !== 'Note' && getType() !== 'Batch Notes' && sheetData[comment.audit_sheet_rows?.[0]] && (
                            <Text fontSize='xs' color='rgba(255,255,255,0.8)' noOfLines={1} minW={0}>
                                {isRowWise
                                    ? `Row ${sheetData[comment.audit_sheet_rows[0]]}`
                                    : `${dynamicAuditColumnHeader[comment.audit_sheet_columns[0]] || comment.audit_sheet_columns[0]} · Row ${sheetData[comment.audit_sheet_rows[0]]}`
                                }
                            </Text>
                        )}
                    </HStack>
                    <HStack spacing={1.5} flexShrink={0}>
                        {getType() !== 'Note' && getType() !== 'Batch Notes' && (
                            <Box px={2} py='1px' borderRadius='full' bg={statusBadgeColor()}>
                                <Text fontSize='10px' fontWeight='bold' color='white' whiteSpace='nowrap'>
                                    {threadStatus}
                                </Text>
                            </Box>
                        )}
                        {isCollapsed
                            ? <ChevronDownIcon color='white' />
                            : <ChevronUpIcon color='white' />
                        }
                    </HStack>
                </HStack>

                {/* Thread body */}
                {!isCollapsed && (
                    <Box>
                        {renderComment({
                            ...comment,
                            isParent: true,
                            justStatus: false,
                            status: comment.action,
                            key: `${index}-${-1}-inline-comment`,
                            index,
                            i: -1,
                        })}
                        {comment.reply_comments?.map((reply, i) =>
                            renderComment({
                                ...reply,
                                isParent: false,
                                justStatus: !reply.comment,
                                status: reply.action,
                                index,
                                i,
                                key: `${index}-${i}-inline-comment`,
                            })
                        )}
                        {comment.audit_sheet_rows.length ? (
                            <Box px={3} pb={3} pt={2} borderTop='1px solid' borderColor='gray.100'>
                                {isRejected ? (
                                    <HStack pl={3} py='3px' borderLeft='3px solid' borderColor={AppColors.danger} spacing={1}>
                                        <Text fontSize='xs' fontWeight='semibold' color={AppColors.danger}>
                                            This rebuttal was rejected — open a new thread to contest
                                        </Text>
                                    </HStack>
                                ) : threadStatus === 'Resolved' ? (
                                    <HStack pl={3} py='3px' borderLeft='3px solid' borderColor={AppColors.darkGray} spacing={1}>
                                        <Text fontSize='xs' fontWeight='semibold' color={AppColors.darkGray}>
                                            This rebuttal has been resolved
                                        </Text>
                                    </HStack>
                                ) : (
                                    <>
                                        {alreadyInClientReview && (
                                            <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor='#d97706' spacing={1}>
                                                <Text fontSize='xs' fontWeight='semibold' color='#b7791f'>
                                                    Under QA review — reply will be added to existing thread
                                                </Text>
                                            </HStack>
                                        )}
                                        {showQAHint && (
                                            <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor={AppColors.secondary} spacing={1}>
                                                <RebuttalIcon style={{ height: 11, width: 11, flexShrink: 0 }} />
                                                <Text fontSize='xs' fontWeight='semibold' color={AppColors.secondary}>
                                                    This reply will be sent to QA for review
                                                </Text>
                                            </HStack>
                                        )}
                                        <CommentInput
                                            message={replyMessage}
                                            setMessage={setReplyMessage}
                                            id={`${index}-mention-input`}
                                            placeholder='Add a message...'
                                        />
                                        {replyMessage && (
                                            <CommentSaveButton
                                                onSave={replyAction}
                                                onCancel={() => setReplyMessage('')}
                                                isLoading={isPostCommentLoading}
                                            />
                                        )}
                                    </>
                                )}
                            </Box>
                        ) : null}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
export default CommentBox
